import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiCall } from '../../api/apiConfig';

// Async thunk for extracting keywords
export const extractKeywords = createAsyncThunk(
  'keywordExtraction/extractKeywords',
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/extract-keywords', 'POST', { text });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error extracting keywords');
    }
  }
);

// Async thunk for batch keyword extraction
export const batchExtractKeywords = createAsyncThunk(
  'keywordExtraction/batchExtractKeywords',
  async ({ texts }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/extract-keywords-batch', 'POST', { texts });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error in batch keyword extraction');
    }
  }
);

// Async thunk for analyzing keywords
export const analyzeKeywords = createAsyncThunk(
  'keywordExtraction/analyzeKeywords',
  async ({ text, target_keywords }, { rejectWithValue }) => {
    try {
      const response = await apiCall('/analyze-keywords', 'POST', { text, target_keywords });
      if (response.error) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue('Error analyzing keywords');
    }
  }
);

const keywordExtractionSlice = createSlice({
  name: 'keywordExtraction',
  initialState: {
    keywords: [], // Array of extracted keywords
    keyphrases: [], // Array of extracted keyphrases
    total_keywords: 0, // Total number of keywords
    total_keyphrases: 0, // Total number of keyphrases
    method: null, // Method used for extraction (AI or fallback)
    model: null, // Model used for extraction
    note: null, // Additional notes
    
    // For batch extraction
    batchResults: [],
    
    // For keyword analysis
    analysisResults: null,
    matches: [],
    missing: [],
    matchRate: 0,
    
    loading: false, // API request state
    error: null, // Error message for failed requests
    currentMode: 'single', // 'single', 'batch', or 'analyze'
  },
  reducers: {
    clearResults: (state) => {
      state.keywords = [];
      state.keyphrases = [];
      state.total_keywords = 0;
      state.total_keyphrases = 0;
      state.method = null;
      state.model = null;
      state.note = null;
      state.batchResults = [];
      state.analysisResults = null;
      state.matches = [];
      state.missing = [];
      state.matchRate = 0;
      state.error = null;
    },
    setMode: (state, action) => {
      state.currentMode = action.payload;
    },
    updateKeywords: (state, action) => {
      state.keywords = action.payload.keywords || [];
      state.keyphrases = action.payload.keyphrases || [];
      state.total_keywords = action.payload.total_keywords || 0;
      state.total_keyphrases = action.payload.total_keyphrases || 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Extract keywords
      .addCase(extractKeywords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(extractKeywords.fulfilled, (state, action) => {
        state.loading = false;
        state.keywords = action.payload.keywords || [];
        state.keyphrases = action.payload.keyphrases || [];
        state.total_keywords = action.payload.total_keywords || 0;
        state.total_keyphrases = action.payload.total_keyphrases || 0;
        state.method = action.payload.method || null;
        state.model = action.payload.model || null;
        state.note = action.payload.note || null;
      })
      .addCase(extractKeywords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to extract keywords';
      })
      
      // Batch extract keywords
      .addCase(batchExtractKeywords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(batchExtractKeywords.fulfilled, (state, action) => {
        state.loading = false;
        state.batchResults = action.payload.results || [];
      })
      .addCase(batchExtractKeywords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to batch extract keywords';
      })
      
      // Analyze keywords
      .addCase(analyzeKeywords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(analyzeKeywords.fulfilled, (state, action) => {
        state.loading = false;
        state.analysisResults = action.payload;
        state.keywords = action.payload.extracted_keywords || [];
        state.keyphrases = action.payload.extracted_keyphrases || [];
        state.matches = action.payload.matches || [];
        state.missing = action.payload.missing || [];
        state.matchRate = parseFloat(action.payload.match_rate) || 0;
        state.method = action.payload.method || null;
        state.model = action.payload.model || null;
        state.note = action.payload.note || null;
      })
      .addCase(analyzeKeywords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to analyze keywords';
      });
  },
});

// Reusable selectors for state access
export const selectKeywords = (state) => state.keywordExtraction.keywords;
export const selectKeyphrases = (state) => state.keywordExtraction.keyphrases;
export const selectLoading = (state) => state.keywordExtraction.loading;
export const selectError = (state) => state.keywordExtraction.error;
export const selectTotalKeywords = (state) => state.keywordExtraction.total_keywords;
export const selectTotalKeyphrases = (state) => state.keywordExtraction.total_keyphrases;
export const selectMethod = (state) => state.keywordExtraction.method;
export const selectModel = (state) => state.keywordExtraction.model;
export const selectNote = (state) => state.keywordExtraction.note;
export const selectBatchResults = (state) => state.keywordExtraction.batchResults;
export const selectAnalysisResults = (state) => state.keywordExtraction.analysisResults;
export const selectMatches = (state) => state.keywordExtraction.matches;
export const selectMissing = (state) => state.keywordExtraction.missing;
export const selectMatchRate = (state) => state.keywordExtraction.matchRate;
export const selectCurrentMode = (state) => state.keywordExtraction.currentMode;

// Export actions
export const { clearResults, setMode, updateKeywords } = keywordExtractionSlice.actions;

export default keywordExtractionSlice.reducer; 